/**
 * MentionRejectForm Component
 *
 * Inline form for rejecting a mention with rejection reason and optional admin notes
 * Shared component used by both MentionReviewDetailPage and BusinessMentionCard
 */
import React from 'react';
import { Box, Stack, Text, Group, Button } from '@mantine/core';
import { IconX } from '@tabler/icons-react';
import { RejectionReasonSelect } from '@/presentation/components/shared/RejectionReasonSelect';

export interface MentionRejectFormProps {
  /**
   * Current rejection reason value
   */
  rejectionReason: string;

  /**
   * Callback when rejection reason changes
   */
  onRejectionReasonChange: (reason: string) => void;

  /**
   * Current admin notes value
   */
  adminNotes: string;

  /**
   * Callback when admin notes change
   */
  onAdminNotesChange: (notes: string) => void;

  /**
   * Callback when reject is confirmed
   */
  onConfirm: () => void;

  /**
   * Callback when cancel is clicked
   */
  onCancel: () => void;

  /**
   * Whether the form is disabled (mutation in progress)
   */
  disabled?: boolean;

  /**
   * Whether mutation is pending (shows loading state)
   */
  isPending?: boolean;
}

/**
 * MentionRejectForm - Reusable rejection form
 */
export const MentionRejectForm: React.FC<MentionRejectFormProps> = ({
  rejectionReason,
  onRejectionReasonChange,
  adminNotes,
  onAdminNotesChange,
  onConfirm,
  onCancel,
  disabled = false,
  isPending = false
}): React.ReactElement => {
  return (
    <Box bg="red.0" p="md" style={{ borderRadius: '8px' }}>
      <Stack gap="md">
        <Text size="sm" fw={500}>
          Confirm Rejection
        </Text>
        <RejectionReasonSelect
          rejectionReason={rejectionReason}
          onRejectionReasonChange={onRejectionReasonChange}
          adminNotes={adminNotes}
          onAdminNotesChange={onAdminNotesChange}
          disabled={disabled || isPending}
        />
        <Group gap="sm">
          <Button
            color="red"
            onClick={onConfirm}
            loading={isPending}
            disabled={!rejectionReason || disabled}
            leftSection={<IconX size={16} />}
          >
            Confirm Rejection
          </Button>
          <Button variant="subtle" onClick={onCancel} disabled={isPending}>
            Cancel
          </Button>
        </Group>
      </Stack>
    </Box>
  );
};
